import { createViewport } from './scene.js';
import { createRegistry } from './registry.js';
import { createCommands } from './commands.js';
import { connectBridge } from './bridge.js';
import { createLocalExecution } from './local-execution.js';
import { createBridgeUrlProvider } from './bridge-url.js';
import { createStatusIndicator } from './status.js';
import { createOutliner } from './outliner.js';
import { createSelectionState } from './selection-state.js';
import { createSelectionView } from './selection.js';
import { createDebugPanel } from './debug.js';
import { createMenuBar, downloadBase64, downloadText, pickTextFile } from './menu.js';
import { downloadGLTF } from './exporters.js';
import { DEFAULT_PANELS } from './panel-layout.js';
import { createPanelManager } from './panels.js';
import { commandsForAction } from './manual-actions.js';
import { createShortcutManager, shortcutFor } from './shortcuts.js';
import { nextEscapeLayer } from './escape-stack.js';
import { createInspector } from './inspector.js';
import { createAddPalette } from './add-palette.js';
import { createTransformTools } from './transform-tools.js';
import { createMeshSelectionState } from './mesh-selection-state.js';
import { createMeshEditView } from './mesh-edit-view.js';
import { createMeshEditTools } from './mesh-edit-tools.js';
import { createMeshEditActions } from './mesh-edit-actions.js';
import { createViewportNavigation } from './viewport-navigation.js';
import { contextMenuKind, createContextMenu, hitNeedsSelect } from './context-menu.js';
import { editorChromeState, editorModeLabel } from './editor-chrome.js';
import { readBuild, formatBuild } from './build-info.js';
import { registerPwa } from './pwa-register.js';
import { frameObjectsBounds, objectsBoundsCenter } from './frame-bounds.js';

const BUILD = readBuild();
globalThis.__ANVIL_BUILD__ = BUILD;
console.info(`[anvil] build ${formatBuild(BUILD)}${BUILD.builtAt ? ` ${BUILD.builtAt}` : ''}`);
registerPwa();

const viewport = createViewport(document.getElementById('viewport'));
const navigation = createViewportNavigation({ viewport });
viewport.onBeforeRender(() => navigation.update());
const panels = createPanelManager({ root: document.body, viewport, storage: localStorage });
panels.register('outliner', document.getElementById('outliner'), DEFAULT_PANELS.outliner);
panels.register('tools', document.getElementById('tools'), DEFAULT_PANELS.tools);
panels.register('inspector', document.getElementById('inspector'), DEFAULT_PANELS.inspector);
panels.register('add', document.getElementById('add-panel'), DEFAULT_PANELS.add);
panels.register('debug', document.getElementById('debug'), DEFAULT_PANELS.debug);
panels.register('command-reference', document.getElementById('command-reference'), {
  mode: 'closed', x: 150, y: 54, width: 650, height: 480,
});

const registry = createRegistry(viewport.content);
const commands = createCommands({ viewport, registry });
const status = createStatusIndicator();
status.setBuild(formatBuild(BUILD));
let outliner;
let selectionView;
let inspector;
let bridge;
let transformTools;
let meshEditView;
let meshEditTools;
let meshEditActions;
let contextMenu;
let connected = false;
let restoreInProgress = false;
let activeSocketGeneration = 0;
let observedAuthorityRevision = null;
let activeTool = 'select';
let historyState = {
  canUndo: false, canRedo: false, undoLabel: null, redoLabel: null, undoDepth: 0, redoDepth: 0,
};

// Object types a primitive/mesh can enter mesh Edit Mode on. Groups have no
// geometry, so they're never eligible — see toEditableMesh's own guard.
const MESH_EDITABLE_TYPES = new Set(['box', 'sphere', 'cylinder', 'mesh']);

// First-slice extrude has no interactive drag/amount UI (see
// docs/superpowers/specs/2026-08-16-mesh-face-extrude-design.md) — a fixed
// default distance along the face's own normal, matching addBox's default
// unit size, keeps the invocation deterministic and undoable.
const EXTRUDE_DEFAULT_DISTANCE = 0.5;

// Same rationale as EXTRUDE_DEFAULT_DISTANCE: no interactive drag/amount UI
// yet, so a fixed default inward offset keeps the invocation deterministic
// and undoable. Kept well under half of addBox's default unit size so the
// default inset is never rejected as "too large for this region" on an
// unscaled box face.
const INSET_DEFAULT_AMOUNT = 0.15;

// Same rationale as EXTRUDE_DEFAULT_DISTANCE/INSET_DEFAULT_AMOUNT: no
// interactive drag UI yet, so a fixed default factor keeps the invocation
// deterministic and undoable. Shrink-only (matching the smallest sensible
// UI — the command API is the way to enlarge, or scale by any explicit
// factor); a mild 0.8 (not e.g. 0.5) keeps repeated presses controllable
// without immediately racing toward the "too large"/degenerate rejection on
// a small or already-tapered region.
const SCALE_DEFAULT_FACTOR = 0.8;

function setDisabled(control, disabled) {
  if (!control) return;
  control.disabled = Boolean(disabled);
  control.setAttribute('aria-disabled', String(Boolean(disabled)));
}

function refreshAccessibility() {
  const tree = document.getElementById('outliner-tree');
  tree.setAttribute('role', 'listbox');
  tree.setAttribute('aria-label', 'Scene objects');
  tree.setAttribute('aria-multiselectable', 'true');
  for (const row of tree.querySelectorAll('.outliner-row')) {
    const selected = row.getAttribute('aria-selected') === 'true';
    const label = row.querySelector('.label')?.textContent ?? row.dataset.id ?? 'Scene object';
    row.setAttribute('role', 'option');
    row.setAttribute('aria-label', `${label} — ${selected ? 'selected' : 'not selected'}`);
  }

  for (const input of document.querySelectorAll('.inspector-field input:not([aria-label])')) {
    const label = input.closest('.inspector-field')?.querySelector('.field-label')?.textContent;
    const legend = input.closest('fieldset')?.querySelector('legend')?.childNodes[0]?.textContent?.trim();
    if (label) input.setAttribute('aria-label', legend ? `${legend} ${label}` : label);
  }
}

function refreshStatus() {
  const editing = meshSelection?.active;
  const toolLabel = editing
    ? `Edit: ${meshSelection.elementType} (${meshSelection.ids.length})`
    : activeTool;
  status.setWorkspace({
    selectedCount: selection?.ids.length ?? 0,
    activeTool: toolLabel,
    editorMode: editorModeLabel({
      meshEditActive: editing,
      elementType: meshSelection?.elementType,
    }),
    transformSpace: viewport.transformControls?.space ?? 'world',
  });
}

function whenReady(action) {
  if (!connected) return undefined;
  return action();
}

/** The single object mesh Edit Mode would act on, or null if none/ambiguous. */
function soleSelectedObject() {
  const selected = selection?.normaliseTopLevel?.() ?? [];
  return selected.length === 1 ? registry.get(selected[0]) : null;
}

function editMeshEligible() {
  if (!connected) return false;
  const object = soleSelectedObject();
  return Boolean(object && MESH_EDITABLE_TYPES.has(object.userData.anvilType));
}

function extrudeFaceEligible() {
  return connected && meshSelection.active && meshSelection.elementType === 'face' && meshSelection.ids.length === 1;
}

// Same selection requirement as extrudeFaceEligible: Inset Face and Extrude
// Face are both single-visible-face region operations, just with a different
// offset direction.
function insetFaceEligible() {
  return connected && meshSelection.active && meshSelection.elementType === 'face' && meshSelection.ids.length === 1;
}

// Same selection requirement again: scale is a third single-visible-face
// region operation, in-plane instead of along the normal.
function scaleFaceEligible() {
  return connected && meshSelection.active && meshSelection.elementType === 'face' && meshSelection.ids.length === 1;
}

// Same selection requirement as the other three face tools: subdivide is a
// fourth single-visible-face region operation, splitting density instead of
// moving geometry.
function subdivideFaceEligible() {
  return connected && meshSelection.active && meshSelection.elementType === 'face' && meshSelection.ids.length === 1;
}

function bevelEdgeEligible() {
  return meshEditActions?.bevelEdgeEligible() ?? false;
}

function loopCutEligible() {
  return meshEditActions?.loopCutEligible() ?? false;
}

function weldEligible() {
  return meshEditActions?.weldEligible() ?? false;
}

function refreshMeshEditUi() {
  const active = meshSelection.active;
  const chrome = editorChromeState({ meshEditActive: active, elementType: meshSelection.elementType });
  const editItem = document.querySelector('.menu-item[data-action="edit-mesh"]');
  if (editItem) {
    editItem.textContent = active ? 'Exit Edit Mode' : 'Edit Mesh';
    setDisabled(editItem, active ? false : !editMeshEligible());
  }
  for (const button of document.querySelectorAll('[data-editor-mode]')) {
    button.setAttribute('aria-current', String(button.dataset.editorMode === chrome.editorMode));
  }
  setDisabled(document.querySelector('[data-editor-mode="mesh"]'), active ? false : !editMeshEligible());
  const meshRail = document.querySelector('.mesh-mode-rail');
  if (meshRail) {
    meshRail.dataset.open = String(chrome.showMeshElements);
    for (const button of meshRail.querySelectorAll('[data-mesh-mode]')) {
      button.setAttribute('aria-pressed', String(active && button.dataset.meshMode === meshSelection.elementType));
      setDisabled(button, !active);
    }
  }
  const faceRail = document.querySelector('.mesh-face-tools');
  if (faceRail) faceRail.dataset.open = String(chrome.showFaceTools);
  const edgeRail = document.querySelector('.mesh-edge-tools');
  if (edgeRail) edgeRail.dataset.open = String(chrome.showEdgeTools);
  // Extrude Face is a no-op outside Face mode with exactly one face selected
  // (extrudeActiveFace()'s own guard) — disable it so a click isn't a silent
  // no-op, the same convention every other menu action here follows.
  const modelingActions = {
    'extrude-face': extrudeFaceEligible,
    'inset-face': insetFaceEligible,
    'scale-face': scaleFaceEligible,
    'subdivide-face': subdivideFaceEligible,
    'bevel-edge': bevelEdgeEligible,
    'loop-cut': loopCutEligible,
    'weld-vertices': weldEligible,
  };
  for (const [action, eligible] of Object.entries(modelingActions)) {
    const disabled = !eligible();
    for (const control of document.querySelectorAll(`[data-action="${action}"]`)) {
      setDisabled(control, disabled);
    }
  }
  contextMenu?.refresh();
}

function refreshActionStates() {
  const selected = selection?.normaliseTopLevel?.() ?? [];
  const selectedGroups = selected.filter((id) => registry.get(id)?.userData.anvilType === 'group');
  const disabled = {
    duplicate: !connected || selected.length === 0,
    mirror: !connected || selected.length === 0,
    group: !connected || selected.length < 2,
    ungroup: !connected || selectedGroups.length === 0,
    delete: !connected || selected.length === 0,
  };
  refreshMeshEditUi();
  for (const [action, state] of Object.entries(disabled)) {
    const item = document.querySelector(`.menu-item[data-action="${action}"]`);
    setDisabled(item, state);
  }
  setDisabled(document.querySelector('.menu-item[data-action="load-scene"]'), !connected);
  setDisabled(document.querySelector('.menu-item[data-action="clear-scene"]'), !connected);
  for (const item of document.querySelectorAll('.menu-item[data-action^="add-"]')) setDisabled(item, !connected);
  for (const button of document.querySelectorAll('#add-palette-body button')) setDisabled(button, !connected);
  for (const button of document.querySelectorAll('#inspector [data-object-action]')) {
    const action = button.dataset.objectAction;
    setDisabled(button, !connected || disabled[action]);
  }
  for (const input of document.querySelectorAll('#inspector input[data-field]:not([type="checkbox"])')) {
    if (!connected) setDisabled(input, true);
    else input.setAttribute('aria-disabled', String(input.disabled));
  }
  for (const button of document.querySelectorAll('#mirror-popover [data-mirror-axis]')) {
    setDisabled(button, !connected || selected.length === 0);
  }
  for (const button of document.querySelectorAll('[data-tool="move"], [data-tool="rotate"], [data-tool="scale"]')) {
    setDisabled(button, !connected || meshSelection.active);
  }
  // 'select' and 'box-select' have no connection requirement of their own,
  // but selectTool() now blocks every object-level tool switch (including
  // 'select') while mesh Edit Mode is active — disable their buttons too so
  // a click isn't a silent no-op.
  for (const button of document.querySelectorAll('[data-tool="select"], [data-tool="box-select"]')) {
    setDisabled(button, meshSelection.active);
  }
  for (const item of document.querySelectorAll('#menubar .menu-item')) {
    item.setAttribute('aria-disabled', String(item.disabled));
  }
  refreshAccessibility();
  refreshStatus();
}

const selection = createSelectionState({
  allIds: () => registry.list().map((object) => object.id),
  parentOf: (id) => registry.parentIdOf(id),
  onChange: () => {
    selectionView?.refresh();
    transformTools?.refresh();
    outliner?.render();
    inspector?.render();
    refreshActionStates();
  },
});

// Independent from `selection` on purpose (see mesh-selection-state.js):
// entering mesh Edit Mode never touches the object-level selection above.
const meshSelection = createMeshSelectionState({
  onChange: () => {
    meshEditView?.refresh();
    meshEditTools?.refresh();
    refreshMeshEditUi();
    refreshActionStates();
  },
});

selectionView = createSelectionView({
  viewport,
  registry,
  state: selection,
  onFrame: (id) => commands.focusObject({ id }),
});
outliner = createOutliner({
  registry,
  selection,
  onSelect: (id, event) => selection.select(id, { toggle: event.shiftKey }),
  onFrame: (id) => commands.focusObject({ id }),
});
outliner.render();
viewport.start();

const debug = createDebugPanel({ registry, getBridge: () => bridge, getBuild: () => BUILD });
document.getElementById('debug').addEventListener('panelchange', (event) => {
  debug.setOpen(event.detail.mode !== 'closed');
});

const undoButton = document.getElementById('undo-button');
const redoButton = document.getElementById('redo-button');
const historyLabel = document.getElementById('history-label');

function updateHistory(history) {
  historyState = { ...historyState, ...history };
  const { undoLabel, redoLabel, undoDepth, redoDepth } = historyState;
  const canUndo = connected && historyState.canUndo;
  const canRedo = connected && historyState.canRedo;
  setDisabled(undoButton, !canUndo);
  setDisabled(redoButton, !canRedo);
  undoButton.title = canUndo ? `Undo: ${undoLabel}` : 'Nothing to undo';
  redoButton.title = canRedo ? `Redo: ${redoLabel}` : 'Nothing to redo';
  historyLabel.textContent = canUndo ? `Undo ${undoLabel} · ${undoDepth}`
    : canRedo ? `Redo ${redoLabel} · ${redoDepth}` : 'No edits';
  const undoItem = document.querySelector('.menu-item[data-action="undo"]');
  const redoItem = document.querySelector('.menu-item[data-action="redo"]');
  setDisabled(undoItem, !canUndo);
  setDisabled(redoItem, !canRedo);
}

function refreshSceneUi() {
  selectionView.refresh();
  transformTools?.refresh();
  meshEditView?.refresh();
  meshEditTools?.refresh();
  outliner.render();
  inspector?.render();
  refreshActionStates();
}

/**
 * Leave mesh Edit Mode. Safe to call any time, including before it was ever
 * entered — every caller here (connection loss, a restore starting, undo of
 * the very toEditableMesh conversion that started editing, Escape) needs a
 * defensive "make sure we're out" more than a "we know we're in."
 */
function exitMeshEditMode() {
  meshEditTools?.cancel();
  if (!meshSelection.exit()) return;
  transformTools?.setSuspended(false);
  selectTool(activeTool);
}

/**
 * Enter mesh Edit Mode on the sole selected object. Converts it via the
 * existing toEditableMesh command first if it's still a parametric
 * primitive — the same one-way, undoable conversion an agent would call —
 * then hands the shared TransformControls instance to mesh-edit-tools.js
 * and suppresses object-level click-select (selectionView) for the duration.
 */
async function enterMeshEditMode() {
  if (!editMeshEligible() || meshSelection.active) return;
  const object = soleSelectedObject();
  const id = object.userData.anvilId;
  const type = object.userData.anvilType;

  clearActionError(document.getElementById('inspector'));
  if (type !== 'mesh') {
    try {
      await bridge.execute('toEditableMesh', { id }, { label: 'Convert to Editable Mesh' });
    } catch (error) {
      reportActionError('toEditableMesh', error);
      return;
    }
  }
  // The object (or the whole connection) could have gone away while the
  // conversion was in flight — a concurrent delete, undo, or disconnect.
  if (!connected || !registry.has(id) || registry.get(id).userData.anvilType !== 'mesh') return;

  transformTools.setSuspended(true);
  selectionView.setTool('mesh-edit');
  meshSelection.enter(id, 'vertex');
}

function toggleMeshEditMode() {
  contextMenu?.close();
  if (meshSelection.active) exitMeshEditMode();
  else void enterMeshEditMode();
}

// Guards extrudeActiveFace() against reentrancy: the command round-trip is
// asynchronous, and meshSelection.ids only advances to the new cap face once
// it resolves. Without this, pressing X twice quickly (exactly the "chain
// another extrude off the tip" gesture the design doc describes) sends two
// `extrudeRegion` calls for the *same* still-selected source face id — the
// first succeeds and removes it, the second then fails on "unknown face id"
// instead of extruding the tip a second time.
let extrudingFace = false;

/**
 * Extrude the visible face containing the selected triangle, by the
 * deterministic default distance along its own normal — the same
 * `extrudeRegion` command an agent would call. Calls `extrudeRegion`, not
 * `extrudeFace`: a human clicking "a face" of a box means the whole visible
 * rectangle (its two coplanar triangles), not just whichever triangle the
 * click happened to land on — see mesh-topology.js's `coplanarRegion` for
 * the discovery this relies on. A no-op unless mesh Edit Mode is in Face
 * mode with exactly one face selected. On success, selects `capFaceId` — one
 * deterministic face from the new cap region — so a repeated press chains
 * another region extrude off the tip; `coplanarRegion` rediscovers the
 * complete new region from that single id, so nothing here needs to track
 * more than one.
 *
 * Deliberately not all of `capFaceIds`: `extrudeFaceEligible()` (and the
 * Extrude Face menu item's disabled state) require *exactly one* selected
 * face id, so selecting a multi-triangle cap in full would immediately make
 * the button ineligible again — breaking the very chaining this exists to
 * support. The cost is cosmetic: mesh-edit-view.js's face highlight renders
 * per selected id, so only one triangle of a multi-triangle cap (e.g. a box
 * quad's two triangles) shows as highlighted after the extrude, even though
 * clicking anywhere on the visible cap — or just pressing X again — acts on
 * the whole region correctly regardless of what's highlighted.
 */
async function extrudeActiveFace() {
  if (extrudingFace || !extrudeFaceEligible()) return;
  const id = meshSelection.meshId;
  const faceId = meshSelection.ids[0];

  clearActionError(document.getElementById('inspector'));
  extrudingFace = true;
  try {
    const result = await bridge.execute(
      'extrudeRegion',
      { id, faceId, distance: EXTRUDE_DEFAULT_DISTANCE },
      { label: 'Extrude Face' },
    );
    // Re-check Face mode too, not just meshId — the selection could have
    // switched to vertex/edge mode (clearing element ids) while this command
    // was in flight, and a face id has no meaning there.
    if (meshSelection.active && meshSelection.meshId === id && meshSelection.elementType === 'face') {
      meshSelection.select(result.capFaceId);
    }
  } catch (error) {
    reportActionError('extrudeRegion', error);
  } finally {
    extrudingFace = false;
  }
}

// Same reentrancy hazard as extrudingFace, for the same reason: the command
// round-trip is asynchronous and meshSelection.ids only advances to the new
// cap face once it resolves.
let insettingFace = false;

/**
 * Inset the visible face containing the selected triangle, by the
 * deterministic default amount — the same `insetRegion` command an agent
 * would call. Calls `insetRegion`, not `insetFace`, for the same reason
 * `extrudeActiveFace` calls `extrudeRegion`: a human clicking "a face" of a
 * box means the whole visible rectangle, not just whichever triangle the
 * click landed on. A no-op unless mesh Edit Mode is in Face mode with
 * exactly one face selected.
 *
 * On success, selects `capFaceId` — one deterministic face from the new
 * inner region — mirroring extrudeActiveFace's selection handoff. Unlike
 * extrude, though, pressing the shortcut again on that same selection will
 * *not* cleanly inset a second time: insetRegion.js documents why (inset
 * stays coplanar with its own ring, so `coplanarRegion` from the new inner
 * face rediscovers the whole original flat area again and the repeat call
 * fails with a clear "vertex interior to its own boundary" error, reported
 * the same way any other rejected command is). That's an intentional,
 * documented limitation, not a bug here.
 */
async function insetActiveFace() {
  if (insettingFace || !insetFaceEligible()) return;
  const id = meshSelection.meshId;
  const faceId = meshSelection.ids[0];

  clearActionError(document.getElementById('inspector'));
  insettingFace = true;
  try {
    const result = await bridge.execute(
      'insetRegion',
      { id, faceId, amount: INSET_DEFAULT_AMOUNT },
      { label: 'Inset Face' },
    );
    if (meshSelection.active && meshSelection.meshId === id && meshSelection.elementType === 'face') {
      meshSelection.select(result.capFaceId);
    }
  } catch (error) {
    reportActionError('insetRegion', error);
  } finally {
    insettingFace = false;
  }
}

// Same reentrancy hazard as extrudingFace/insettingFace, for the same reason.
let scalingFace = false;

/**
 * Scale the visible face containing the selected triangle, by the
 * deterministic default factor — the same `scaleRegion` command an agent
 * would call. Calls `scaleRegion`, not `scaleFace`, for the same reason
 * `extrudeActiveFace`/`insetActiveFace` call their own region variants: a
 * human clicking "a face" of a box means the whole visible rectangle, not
 * just whichever triangle the click landed on. A no-op unless mesh Edit
 * Mode is in Face mode with exactly one face selected.
 *
 * Unlike extrude/inset, scale never replaces or renames any face — it only
 * moves the existing region's own vertices — so there is no cap id to hand
 * back and re-select: the same `meshSelection.ids` a caller already has
 * stay valid and selected after a successful scale, and pressing the
 * shortcut again scales the same face further, no re-chaining needed.
 */
async function scaleActiveFace() {
  if (scalingFace || !scaleFaceEligible()) return;
  const id = meshSelection.meshId;
  const faceId = meshSelection.ids[0];

  clearActionError(document.getElementById('inspector'));
  scalingFace = true;
  try {
    await bridge.execute(
      'scaleRegion',
      { id, faceId, factor: SCALE_DEFAULT_FACTOR },
      { label: 'Scale Face' },
    );
  } catch (error) {
    reportActionError('scaleRegion', error);
  } finally {
    scalingFace = false;
  }
}

// Same reentrancy hazard as extrudingFace/insettingFace/scalingFace, for the
// same reason: the command round-trip is asynchronous and meshSelection.ids
// only advances to the new center face once it resolves.
let subdividingFace = false;

/**
 * Subdivide the visible face containing the selected triangle into 4 — the
 * same `subdivideRegion` command an agent would call. Calls `subdivideRegion`,
 * not `subdivideFace`, for the same reason `extrudeActiveFace`/`insetActiveFace`/
 * `scaleActiveFace` call their own region variants: a human clicking "a face"
 * of a box means the whole visible rectangle, not just whichever triangle the
 * click landed on. A no-op unless mesh Edit Mode is in Face mode with exactly
 * one face selected.
 *
 * Unlike extrude/inset/scale, there is no default distance/amount/factor to
 * choose — subdivision is a single fixed operation. On success, selects the
 * returned `centerFaceId` — one deterministic face from the new center
 * triangles — so a repeated press keeps subdividing further, the same
 * chaining shape `extrudeActiveFace`/`insetActiveFace` use.
 */
async function subdivideActiveFace() {
  if (subdividingFace || !subdivideFaceEligible()) return;
  const id = meshSelection.meshId;
  const faceId = meshSelection.ids[0];

  clearActionError(document.getElementById('inspector'));
  subdividingFace = true;
  try {
    const result = await bridge.execute(
      'subdivideRegion',
      { id, faceId },
      { label: 'Subdivide Face' },
    );
    if (meshSelection.active && meshSelection.meshId === id && meshSelection.elementType === 'face') {
      meshSelection.select(result.centerFaceId);
    }
  } catch (error) {
    reportActionError('subdivideRegion', error);
  } finally {
    subdividingFace = false;
  }
}

updateHistory({ canUndo: false, canRedo: false, undoLabel: null, redoLabel: null });

function applyConnectionState(state, detail, { generation } = {}) {
  // A socket-open event precedes the authoritative restore. Keep mutations
  // disabled until onRestore has rebuilt the local scene.
  connected = false;
  if (Number.isInteger(generation)) activeSocketGeneration = generation;
  document.body.dataset.bridgeState = state;
  status.setState(state, detail);
  debug.setConnection(state, detail);
  if (state !== 'connected') {
    exitMeshEditMode();
    if (transformTools) {
      transformTools.cancel();
      selectTool('select');
    }
  }
  inspector?.render();
  updateHistory(historyState);
  refreshActionStates();
}

bridge = connectBridge({
  commands,
  registry,
  resolveUrl: createBridgeUrlProvider(),
  onStatus: applyConnectionState,
  onCommand: (cmd, ok, detail) => {
    status.setLastCommand(cmd, ok);
    debug.record(cmd, ok, detail);
    if (ok) refreshSceneUi();
  },
  onHandshake: ({ generation, revision, authorityRevision }) => {
    if (generation !== activeSocketGeneration || restoreInProgress ||
        document.body.dataset.bridgeState !== 'connected' ||
        revision !== authorityRevision || authorityRevision !== observedAuthorityRevision) return;
    connected = true;
    inspector?.render();
    updateHistory(historyState);
    refreshActionStates();
  },
  onRestoreStart: () => {
    // Live undo/redo/load restores do not change socket state. Revoke local
    // mutation authority before clearScene or any asynchronous replay step.
    // A restore can turn an editable mesh back into a primitive (undoing the
    // very toEditableMesh call that started editing) or delete it outright,
    // so edit mode always exits rather than trying to survive the rebuild.
    connected = false;
    restoreInProgress = true;
    exitMeshEditMode();
    if (transformTools) {
      transformTools.cancel();
      selectTool('select');
    }
    inspector?.render();
    updateHistory(historyState);
    refreshActionStates();
  },
  onRestore: (count, { generation, revision, authorityRevision }) => {
    restoreInProgress = false;
    connected = generation === activeSocketGeneration &&
      document.body.dataset.bridgeState === 'connected' &&
      revision === authorityRevision && authorityRevision === observedAuthorityRevision;
    status.setRestored(count);
    refreshSceneUi();
    updateHistory(historyState);
    debug.refresh();
  },
  onCamera: (pose) => viewport.setCameraPose(pose),
  onHistory: (history) => {
    updateHistory(history);
    debug.refresh();
  },
  onStats: (stats) => {
    observedAuthorityRevision = stats.revision;
    debug.refresh();
  },
});
const disconnectBridge = bridge.disconnect;
bridge.disconnect = () => {
  disconnectBridge();
  applyConnectionState('disconnected', 'connection closed');
};

// Local Execution / Cloud Sync v1 — the local-hot path for addBox/moveObject
// only. Every other command, and every other UI action, stays on the gated
// bridge.execute()/executeBatch() path above unchanged. See
// docs/superpowers/plans/2026-08-20-local-execution-cloud-sync-v1.md.
const localExecution = createLocalExecution({
  commands,
  registry,
  bridge,
  // Reuses the exact same post-command UI refresh a gated bridge.execute()
  // success already triggers via onCommand above — never a second,
  // duplicated implementation of what "the scene changed" means for the UI.
  onLocalCommand: (cmd) => {
    status.setLastCommand(cmd, true);
    debug.record(cmd, true);
    refreshSceneUi();
  },
});

transformTools = createTransformTools({ viewport, registry, selection, bridge, localExecution });
meshEditView = createMeshEditView({ viewport, registry, meshSelection });
meshEditActions = createMeshEditActions({
  meshSelection,
  bridge,
  isReady: () => connected,
  refresh: () => meshEditView.refresh(),
  reportError: reportActionError,
  onStateChange: refreshActionStates,
});
meshEditTools = createMeshEditTools({ viewport, registry, meshSelection, bridge });
meshEditTools.onPreview(() => meshEditView.refresh());
bridge.beforeCommand(() => {
  transformTools.cancel();
  meshEditTools.cancel();
});

inspector = createInspector({
  root: document.getElementById('inspector'), selection, registry, bridge,
  isReady: () => connected,
});
const addPalette = createAddPalette({
  root: document.getElementById('add-panel'), viewport, bridge, selection, localExecution,
});

document.getElementById('add-panel').addEventListener('addpaletteopen', () => panels.toggle('add', true));
document.getElementById('add-panel').addEventListener('addpaletteclose', () => panels.toggle('add', false));
document.body.addEventListener('manualerror', (event) => {
  debug.record(`manual:${event.detail.action}`, false, event.detail.error);
});

function clearActionError(root) {
  if (!root) return;
  root.dataset.error = 'false';
  const target = root.querySelector('.action-error');
  if (target) target.textContent = '';
}

function reportActionError(action, error, root = document.getElementById('inspector')) {
  const message = error?.message ?? String(error);
  let target = root.querySelector('.panel-error, .field-error, .action-error');
  if (!target) {
    target = document.createElement('div');
    target.className = 'action-error';
    target.setAttribute('role', 'alert');
    root.append(target);
  }
  target.textContent = message;
  root.dataset.error = 'true';
  debug.record(`manual:${action}`, false, message);
}

function setSelection(ids) {
  selection.clear();
  ids.filter((id) => registry.has(id)).forEach((id, index) => selection.select(id, { toggle: index > 0 }));
}

function resultEntries(result, batched) {
  return batched ? result.entries.map((entry) => entry.result).filter(Boolean) : [result];
}

async function runObjectAction(action, options = {}, context = {}) {
  if (!connected) return;
  if (action === 'mirror' && !options.axis) {
    openMirror();
    return;
  }
  let ids = selection.normaliseTopLevel();
  if (action === 'ungroup') {
    ids = ids.filter((id) => registry.get(id)?.userData.anvilType === 'group');
  }
  if (ids.length === 0 || (action === 'group' && ids.length < 2)) return;
  const plan = commandsForAction(action, ids, options);
  const batched = ids.length > 1 || plan.entries.length > 1;
  const errorRoot = context.root ?? document.getElementById('inspector');
  clearActionError(errorRoot);
  try {
    let result;
    if (batched) result = await bridge.executeBatch(plan.entries, { label: plan.label });
    else result = await bridge.execute(plan.entries[0][0], plan.entries[0][1], { label: plan.label });
    const entries = resultEntries(result, batched);
    if (action === 'duplicate') setSelection(entries.flatMap((entry) => entry.created ?? []));
    else if (action === 'mirror') setSelection(entries.map((entry) => entry.id).filter(Boolean));
    else if (action === 'group') setSelection(entries.map((entry) => entry.id).filter(Boolean));
    else if (action === 'ungroup') setSelection(entries.flatMap((entry) => entry.freed ?? []));
    else selection.prune();
  } catch (error) {
    if (context.reopen) errorRoot.dataset.open = 'true';
    reportActionError(action, error, errorRoot);
  }
}

function frameSelection() {
  const objects = selection.normaliseTopLevel().map((id) => registry.get(id)).filter(Boolean);
  if (objects.length === 0) return;
  if (objects.length === 1) {
    commands.focusObject({ id: objects[0].userData.anvilId });
    return;
  }
  frameObjectsBounds({ objects, camera: viewport.camera, controls: viewport.controls });
}

function frameAll() {
  if (registry.size > 0) commands.focusObject({});
}

function selectTool(tool) {
  // Every object-level tool, including 'select', is meaningless mid mesh-edit
  // (transformTools is suspended and canvas clicks are redirected to
  // mesh-edit-view) — a bare 'select' must be blocked here too, not just
  // move/rotate/scale/box-select, or pressing Q (or clicking the Select
  // button, never disabled during Edit Mode) would un-suppress object-level
  // click-select via selectionView.setTool(null) while meshSelection is
  // still active and mesh-edit-view's own click listener is still live,
  // making a single canvas click react as both a vertex/edge/face pick and
  // an object reselection. exitMeshEditMode() calls meshSelection.exit()
  // before calling back in here, so by the time it does, active is already
  // false and this guard doesn't block it.
  if (meshSelection.active) return;
  if (!connected && ['move', 'rotate', 'scale'].includes(tool)) return;
  activeTool = tool;
  selectionView.setTool(tool === 'select' ? null : tool);
  transformTools.setTool(tool === 'box-select' ? 'box' : tool);
  for (const button of document.querySelectorAll('[data-tool]')) {
    button.setAttribute('aria-pressed', String(button.dataset.tool === tool));
  }
  refreshStatus();
}
document.getElementById('tools').addEventListener('click', (event) => {
  const modeButton = event.target.closest('[data-editor-mode]');
  if (modeButton) {
    const wantEdit = modeButton.dataset.editorMode === 'mesh';
    if (wantEdit !== meshSelection.active) toggleMeshEditMode();
    return;
  }
  const toolButton = event.target.closest('[data-tool]');
  if (toolButton) {
    selectTool(toolButton.dataset.tool);
    return;
  }
  const meshModeButton = event.target.closest('[data-mesh-mode]');
  if (meshModeButton && meshSelection.active) {
    meshSelection.setElementType(meshModeButton.dataset.meshMode);
    return;
  }
  const actionButton = event.target.closest('[data-action]');
  if (!actionButton || actionButton.disabled) return;
  if (actionButton.dataset.action === 'extrude-face') void extrudeActiveFace();
  if (actionButton.dataset.action === 'inset-face') void insetActiveFace();
  if (actionButton.dataset.action === 'scale-face') void scaleActiveFace();
  if (actionButton.dataset.action === 'subdivide-face') void subdivideActiveFace();
  if (actionButton.dataset.action === 'bevel-edge') void meshEditActions.bevelEdge();
  if (actionButton.dataset.action === 'loop-cut') void meshEditActions.loopCut();
  if (actionButton.dataset.action === 'weld-vertices') void meshEditActions.weld();
});

const mirrorPopover = document.getElementById('mirror-popover');
const pivotMode = document.getElementById('mirror-pivot-mode');
const pivotValue = document.getElementById('mirror-pivot-value');
function openMirror() {
  if (!connected) return;
  if (selection.normaliseTopLevel().length === 0) return;
  mirrorPopover.dataset.open = 'true';
  mirrorPopover.querySelector('.panel-error').textContent = '';
}
function closeMirror() {
  mirrorPopover.dataset.open = 'false';
}
pivotMode.addEventListener('change', () => {
  pivotValue.disabled = pivotMode.value !== 'exact';
  if (!pivotValue.disabled) pivotValue.focus();
});
mirrorPopover.addEventListener('click', (event) => {
  const button = event.target.closest('[data-mirror-axis]');
  if (!button || !connected || button.disabled) return;
  const axis = button.dataset.mirrorAxis;
  const axisIndex = { x: 0, y: 1, z: 2 }[axis];
  let pivot = 0;
  if (pivotMode.value === 'selection') {
    const objects = selection.normaliseTopLevel().map((id) => registry.get(id)).filter(Boolean);
    pivot = objectsBoundsCenter(objects).getComponent(axisIndex);
  } else if (pivotMode.value === 'exact') {
    if (pivotValue.value.trim() === '' || !Number.isFinite(Number(pivotValue.value))) {
      mirrorPopover.querySelector('.panel-error').textContent = 'Pivot must be a finite number.';
      return;
    }
    pivot = Number(pivotValue.value);
  }
  closeMirror();
  void runObjectAction('mirror', { axis, pivot }, { root: mirrorPopover, reopen: true });
});

document.getElementById('inspector').addEventListener('objectaction', (event) => {
  void runObjectAction(event.detail.action, {}, { root: document.getElementById('inspector') });
});

function safeHistory(action, root = document.getElementById('history-controls')) {
  if (!connected) return Promise.resolve();
  clearActionError(root);
  return action().catch((error) => reportActionError('history', error, root));
}
undoButton.addEventListener('click', () => safeHistory(() => bridge.undo()));
redoButton.addEventListener('click', () => safeHistory(() => bridge.redo()));
viewport.onCameraChange((pose) => bridge.reportCamera(pose));
navigation.onFlyEnd((pose) => bridge.reportCamera(pose));
navigation.onStillClick((point) => {
  const event = { clientX: point.x, clientY: point.y };
  let hitFaceId = null;
  let hitEdgeId = null;
  let hitObjectId = null;
  if (meshSelection.active && meshSelection.elementType === 'face') {
    hitFaceId = meshEditView.pickAt(event);
    if (hitNeedsSelect(meshSelection.ids, hitFaceId)) meshSelection.select(hitFaceId);
  } else if (meshSelection.active && meshSelection.elementType === 'edge') {
    hitEdgeId = meshEditView.pickAt(event);
    if (hitNeedsSelect(meshSelection.ids, hitEdgeId)) meshSelection.select(hitEdgeId);
  } else if (!meshSelection.active) {
    hitObjectId = selectionView.idAt(event);
    if (hitNeedsSelect(selection.ids, hitObjectId)) selection.select(hitObjectId);
  }
  const kind = contextMenuKind({
    meshEditActive: meshSelection.active,
    elementType: meshSelection.elementType,
    hitFaceId,
    hitEdgeId,
    hitObjectId,
  });
  menuBar.closeAll();
  contextMenu.open({ kind, x: point.x, y: point.y });
});
viewport.onContextChange((state) => status.setContext(state));

function timestamp() {
  return new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
}

function actionDisabled() {
  const selected = selection?.normaliseTopLevel?.() ?? [];
  const selectedGroups = selected.filter((id) => registry.get(id)?.userData.anvilType === 'group');
  return {
    duplicate: !connected || selected.length === 0,
    mirror: !connected || selected.length === 0,
    group: !connected || selected.length < 2,
    ungroup: !connected || selectedGroups.length === 0,
    delete: !connected || selected.length === 0,
    'edit-mesh': meshSelection.active ? false : !editMeshEligible(),
    'extrude-face': !extrudeFaceEligible(),
    'inset-face': !insetFaceEligible(),
    'scale-face': !scaleFaceEligible(),
    'subdivide-face': !subdivideFaceEligible(),
    'bevel-edge': !bevelEdgeEligible(),
    'loop-cut': !loopCutEligible(),
    'frame-selection': selected.length === 0,
    'frame-all': registry.size === 0,
    'add-box': !connected,
    'add-sphere': !connected,
    'add-cylinder': !connected,
  };
}

contextMenu = createContextMenu({
  getDisabled: actionDisabled,
  actions: {
    'edit-mesh': () => toggleMeshEditMode(),
    duplicate: () => whenReady(() => runObjectAction('duplicate')),
    mirror: () => whenReady(() => openMirror()),
    group: () => whenReady(() => runObjectAction('group')),
    ungroup: () => whenReady(() => runObjectAction('ungroup')),
    delete: () => whenReady(() => runObjectAction('delete')),
    'extrude-face': () => void extrudeActiveFace(),
    'inset-face': () => void insetActiveFace(),
    'scale-face': () => void scaleActiveFace(),
    'subdivide-face': () => void subdivideActiveFace(),
    'bevel-edge': () => void meshEditActions.bevelEdge(),
    'loop-cut': () => void meshEditActions.loopCut(),
    'frame-selection': () => frameSelection(),
    'frame-all': () => frameAll(),
    'add-box': () => addPalette.add('box'),
    'add-sphere': () => addPalette.add('sphere'),
    'add-cylinder': () => addPalette.add('cylinder'),
  },
});

const menuBar = createMenuBar({
  'save-scene': async () => {
    const session = await bridge.requestSession();
    downloadText(
      `anvil-scene-${timestamp()}.json`,
      JSON.stringify({ version: session.version, log: session.log, camera: session.camera }, null, 2),
    );
  },
  'load-scene': async () => {
    const text = await pickTextFile();
    if (!text) return;
    // The bridge owns the scene, so loading means replacing its log; every
    // viewer rebuilds from the revision bump that follows.
    await bridge.loadSession(JSON.parse(text));
  },
  'save-screenshot': () => {
    const result = commands.screenshot();
    downloadBase64(`anvil-viewport-${timestamp()}.png`, result.base64, 'image/png');
  },
  'export-glb': () => downloadGLTF(viewport.content, { binary: true }),
  'export-gltf': () => downloadGLTF(viewport.content, { binary: false }),

  'clear-scene': async () => {
    if (!confirm('Delete every object in the scene?')) return;
    // clearScene is a bridge-owned, labeled history entry — unlike loading an
    // empty session, it stays undoable and keeps its own label in the log.
    await bridge.execute('clearScene', {}, { label: 'Clear Scene' });
    selection.clear();
  },
  undo: (item) => safeHistory(() => bridge.undo(), item.closest('.menu')),
  redo: (item) => safeHistory(() => bridge.redo(), item.closest('.menu')),
  'add-box': () => addPalette.add('box'),
  'add-sphere': () => addPalette.add('sphere'),
  'add-cylinder': () => addPalette.add('cylinder'),
  duplicate: (item) => runObjectAction('duplicate', {}, { root: item.closest('.menu') }),
  mirror: () => openMirror(),
  'edit-mesh': () => toggleMeshEditMode(),
  'bevel-edge': () => void meshEditActions.bevelEdge(),
  'loop-cut': () => void meshEditActions.loopCut(),
  'extrude-face': () => void extrudeActiveFace(),
  'inset-face': () => void insetActiveFace(),
  'scale-face': () => void scaleActiveFace(),
  'subdivide-face': () => void subdivideActiveFace(),
  group: (item) => runObjectAction('group', {}, { root: item.closest('.menu') }),
  ungroup: (item) => runObjectAction('ungroup', {}, { root: item.closest('.menu') }),
  'select-all': () => handleSelectAll(),
  'clear-selection': () => handleClearSelection(),
  delete: (item) => runObjectAction('delete', {}, { root: item.closest('.menu') }),
  'frame-selection': () => frameSelection(),
  'frame-all': () => frameAll(),
  'toggle-outliner': () => panels.toggle('outliner'),
  'toggle-inspector': () => panels.toggle('inspector'),
  'toggle-add': () => addPalette.openAt({}),
  'toggle-tools': () => panels.toggle('tools'),
  debug: () => panels.toggle('debug'),
  'command-reference': () => panels.toggle('command-reference', true),
  'reset-workspace': () => panels.reset(),
  about: () => alert(
    'Anvil - a Three.js scene editor driven live from the command line.\n\n' +
      `bridge: ${bridge.url}\nCommands: node tools/anvil.mjs help`,
  ),
});

function isAddPanelOpen() {
  return document.getElementById('add-panel')?.dataset.panelMode !== 'closed';
}

function currentMeshElementIds() {
  return meshEditView?.currentElementIds() ?? [];
}

function handleSelectAll() {
  if (meshSelection.active) {
    meshSelection.selectAll(currentMeshElementIds());
    return;
  }
  selection.selectAll();
}

function handleClearSelection() {
  if (meshSelection.active) meshSelection.clear();
  else selection.clear();
}

function handleCancel() {
  const layer = nextEscapeLayer({
    contextMenuOpen: contextMenu.isOpen,
    mirrorOpen: mirrorPopover.dataset.open === 'true',
    addPaletteOpen: isAddPanelOpen(),
    transformDragging: transformTools.dragging,
    meshEditDragging: meshEditTools.dragging,
    flyHeld: navigation.isRmbHeld(),
    hasSelection: meshSelection.active
      ? meshSelection.ids.length > 0
      : selection.ids.length > 0,
    meshEditActive: meshSelection.active,
  });
  if (layer === 'close-context-menu') {
    contextMenu.close();
    return;
  }
  if (layer === 'close-mirror') {
    closeMirror();
    return;
  }
  if (layer === 'close-add-palette') {
    addPalette.close();
    return;
  }
  if (layer === 'cancel-transform') {
    transformTools.cancel();
    return;
  }
  if (layer === 'cancel-mesh-edit') {
    meshEditTools.cancel();
    return;
  }
  if (layer === 'exit-fly') {
    navigation.abortHeldRmb();
    return;
  }
  if (layer === 'clear-selection') {
    handleClearSelection();
    return;
  }
  if (layer === 'exit-edit-mesh') {
    exitMeshEditMode();
  }
}

const shortcuts = createShortcutManager({
  target: document,
  isBlocked: (event) => navigation.isRmbHeld() && shortcutFor(event) !== 'cancel',
  actions: {
    undo: () => whenReady(() => safeHistory(() => bridge.undo())),
    redo: () => whenReady(() => safeHistory(() => bridge.redo())),
    'select-tool': () => selectTool('select'),
    'move-tool': () => selectTool('move'),
    'rotate-tool': () => selectTool('rotate'),
    'scale-tool': () => selectTool('scale'),
    'box-select': () => selectTool('box-select'),
    'frame-selection': () => frameSelection(),
    'frame-all': () => frameAll(),
    'add-palette': (event) => addPalette.openAt(event),
    delete: () => whenReady(() => runObjectAction('delete')),
    duplicate: () => whenReady(() => runObjectAction('duplicate')),
    group: () => whenReady(() => runObjectAction('group')),
    ungroup: () => whenReady(() => runObjectAction('ungroup')),
    mirror: () => whenReady(() => openMirror()),
    'select-all': () => handleSelectAll(),
    'clear-selection': () => handleClearSelection(),
    'toggle-edit-mode': () => toggleMeshEditMode(),
    'vertex-mode': () => meshSelection.active && meshSelection.setElementType('vertex'),
    'edge-mode': () => meshSelection.active && meshSelection.setElementType('edge'),
    'face-mode': () => meshSelection.active && meshSelection.setElementType('face'),
    'extrude-face': () => void extrudeActiveFace(),
    'inset-face': () => void insetActiveFace(),
    'scale-face': () => void scaleActiveFace(),
    cancel: () => handleCancel(),
  },
});

refreshActionStates();
window.anvil = {
  viewport, registry, commands, bridge, outliner, selection, selectionView, debug, panels,
  inspector, addPalette, shortcuts, transformTools, navigation, contextMenu,
  meshSelection, meshEditView, meshEditTools, meshEditActions,
  get activeTool() { return activeTool; },
  runObjectAction,
  enterMeshEditMode,
  exitMeshEditMode,
};
